import {
  APIProvider,
  GoogleMapsContext,
  AdvancedMarker as ReactGoogleAdvancedMarker,
  Map as ReactGoogleMap,
  type MapProps as ReactGoogleMapProps,
  latLngEquals,
} from "@vis.gl/react-google-maps";
import {
  type Ref,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import usePrefersColorScheme from "~/hooks/usePrefersColorScheme";
import { useRootLoaderData } from "~/remix-lib";
import ClientOnly from "./ClientOnly";

const COLOR_SCHEME_MAP = {
  light: "LIGHT",
  dark: "DARK",
} as const;

export type GoogleMapProps = Omit<
  /**
   * @see https://developers.google.com/maps/documentation/javascript/reference/map?hl=ko#MapOptions
   */
  ReactGoogleMapProps,
  "colorScheme" | "disableDefaultUI" | "mapId" | "keyboardShortcuts"
> & {
  mapSibilings?: React.ReactNode;
};

export function GoogleMap(props: GoogleMapProps) {
  const { googleMap } = useRootLoaderData();

  if (!googleMap) {
    throw new Error("GoogleMap is not configured");
  }

  const colorScheme = usePrefersColorScheme();

  const { mapSibilings, ...restProps } = props;

  return (
    <ClientOnly>
      <APIProvider apiKey={googleMap.key}>
        <ReactGoogleMap
          {...restProps}
          // NOTE: ReactGoogleAdvancedMarker 을 사용하는 경우 필요해요
          mapId={googleMap?.id}
          disableDefaultUI={true}
          colorScheme={colorScheme ? COLOR_SCHEME_MAP[colorScheme] : "LIGHT"}
          keyboardShortcuts={false}
        />
        {mapSibilings}
      </APIProvider>
    </ClientOnly>
  );
}

export const GoogleMarker = ReactGoogleAdvancedMarker;

/**
 * @reference https://github.com/visgl/react-google-maps/blob/b9760e98e1fd8f635b68ac7279d4280661db3fcf/examples/geometry/src/components/circle.tsx
 */
type CircleEventProps = {
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onDrag?: (e: google.maps.MapMouseEvent) => void;
  onDragStart?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (e: google.maps.MapMouseEvent) => void;
  onMouseOver?: (e: google.maps.MapMouseEvent) => void;
  onMouseOut?: (e: google.maps.MapMouseEvent) => void;
  onRadiusChanged?: (r: ReturnType<google.maps.Circle["getRadius"]>) => void;
  onCenterChanged?: (p: ReturnType<google.maps.Circle["getCenter"]>) => void;
};

export type CircleProps = google.maps.CircleOptions & CircleEventProps;

export type CircleRef = Ref<google.maps.Circle | null>;

function useCircle(props: CircleProps) {
  const {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    onRadiusChanged,
    onCenterChanged,
    radius,
    center,
    ...circleOptions
  } = props;
  // This is here to avoid triggering the useEffect below when the callbacks change (which happen if the user didn't memoize them)
  const callbacks = useRef<Record<string, (e: unknown) => void>>({});
  Object.assign(callbacks.current, {
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    onRadiusChanged,
    onCenterChanged,
  });

  const circle = useRef(new google.maps.Circle()).current;
  // update circleOptions (note the dependencies aren't properly checked
  // here, we just assume that setOptions is smart enough to not waste a
  // lot of time updating values that didn't change)
  circle.setOptions(circleOptions);

  useEffect(() => {
    if (!center) return;
    if (!latLngEquals(center, circle.getCenter())) circle.setCenter(center);
  }, [center]);

  useEffect(() => {
    if (radius === undefined || radius === null) return;
    if (radius !== circle.getRadius()) circle.setRadius(radius);
  }, [radius]);

  const map = useContext(GoogleMapsContext)?.map;

  // create circle instance and add to the map once the map is available
  useEffect(() => {
    if (!map) {
      if (map === undefined)
        console.error("<Circle> has to be inside a Map component.");

      return;
    }

    circle.setMap(map);

    return () => {
      circle.setMap(null);
    };
  }, [map]);

  // attach and re-attach event-handlers when any of the properties change
  useEffect(() => {
    if (!circle) return;

    // Add event listeners
    const gme = google.maps.event;

    for (const [eventName, eventCallback] of [
      ["click", "onClick"],
      ["drag", "onDrag"],
      ["dragstart", "onDragStart"],
      ["dragend", "onDragEnd"],
      ["mouseover", "onMouseOver"],
      ["mouseout", "onMouseOut"],
    ]) {
      gme.addListener(circle, eventName, (e: google.maps.MapMouseEvent) => {
        const callback = callbacks.current[eventCallback];
        if (callback) callback(e);
      });
    }
    gme.addListener(circle, "radius_changed", () => {
      const newRadius = circle.getRadius();
      callbacks.current.onRadiusChanged?.(newRadius);
    });
    gme.addListener(circle, "center_changed", () => {
      const newCenter = circle.getCenter();
      callbacks.current.onCenterChanged?.(newCenter);
    });

    return () => {
      gme.clearInstanceListeners(circle);
    };
  }, [circle]);

  return circle;
}

/**
 * Component to render a circle on a map
 */
export const GoogleCircle = forwardRef(
  (
    props: Omit<
      CircleProps,
      "fillColor" | "fillOpacity" | "strokeOpacity" | "strokePosition"
    >,
    ref: CircleRef,
  ) => {
    const { strokeWeight = 0, ...rest } = props;
    const circle = useCircle({
      ...rest,
      fillColor: "#FF6F0F",
      fillOpacity: 0.2,
      strokeWeight,
    });

    useImperativeHandle(ref, () => circle);

    return null;
  },
);
